import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import { useState } from "react";
import type { UtilizationData, ReserveData, ProtocolFilter } from "../types";

interface UtilizationSectionProps {
  utilization: UtilizationData;
  protocolFilter: ProtocolFilter;
}

export function UtilizationSection({ utilization, protocolFilter }: UtilizationSectionProps) {
  const [expandedReserve, setExpandedReserve] = useState<string | null>(null);

  const reserves = utilization.reserves
    .filter((r) => protocolFilter === "all" || r.protocol === protocolFilter)
    .filter((r) => r.total_supplied_usd >= 1_000_000)
    .sort((a, b) => b.total_supplied_usd - a.total_supplied_usd);

  return (
    <div className="card">
      <h2 className="text-base font-semibold mb-1">Utilization & Interest Rates</h2>
      <p className="text-xs text-text-muted mb-3">
        Click any row to view its interest rate curve
      </p>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-text-muted font-medium">
              <th className="text-left py-2">Asset</th>
              <th className="text-right py-2">Supplied</th>
              <th className="text-right py-2">Borrowed</th>
              <th className="text-right py-2">Utilization</th>
              <th className="text-right py-2">Supply APY</th>
              <th className="text-right py-2">Borrow APY</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {reserves.map((r, i) => {
              const key = `${r.protocol}-${r.asset}-${i}`;
              const isExpanded = expandedReserve === key;
              return (
                <ReserveRow
                  key={key}
                  reserve={r}
                  rowKey={key}
                  isExpanded={isExpanded}
                  onToggle={() => setExpandedReserve(isExpanded ? null : key)}
                  colSpan={6}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReserveRow({
  reserve,
  rowKey: _rowKey,
  isExpanded,
  onToggle,
  colSpan,
}: {
  reserve: ReserveData;
  rowKey: string;
  isExpanded: boolean;
  onToggle: () => void;
  colSpan: number;
}) {
  const r = reserve;
  const pct = Math.min(r.utilization * 100, 100);
  const barColor =
    pct > 90 ? "bg-risk-danger" :
    pct > 80 ? "bg-risk-warning" :
    pct > 60 ? "bg-risk-moderate" :
    "bg-risk-safe";

  return (
    <>
      <tr
        className={`border-t border-surface-600 cursor-pointer transition-colors ${isExpanded ? "bg-surface-700" : "hover:bg-surface-700/50"}`}
        onClick={onToggle}
      >
        <td className="py-2 font-sans font-medium text-text-primary">
          <span className="mr-1 text-text-muted text-[10px]">{isExpanded ? "▼" : "▶"}</span>
          {r.asset}
        </td>
        <td className="text-right py-2 text-text-secondary">{formatUsd(r.total_supplied_usd)}</td>
        <td className="text-right py-2 text-text-secondary">{formatUsd(r.total_borrowed_usd)}</td>
        <td className="text-right py-2">
          <div className="flex items-center gap-2 justify-end">
            <span className="text-text-secondary">{pct.toFixed(1)}%</span>
            <div className="w-12 h-1.5 bg-surface-600 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        </td>
        <td className="text-right py-2 text-risk-safe">{(r.supply_apy * 100).toFixed(2)}%</td>
        <td className="text-right py-2 text-risk-warning">{(r.borrow_apy * 100).toFixed(2)}%</td>
      </tr>
      {isExpanded && (
        <tr className="bg-surface-700">
          <td colSpan={colSpan} className="px-4 py-3">
            <RateCurveInline reserve={r} />
          </td>
        </tr>
      )}
    </>
  );
}

function RateCurveInline({ reserve }: { reserve: ReserveData }) {
  const curve = reserve.rate_curve;
  if (!curve || !curve.utilization_points?.length) {
    return <p className="text-xs text-text-muted">Rate curve data not available</p>;
  }

  const chartData = curve.utilization_points.map((u, i) => ({
    utilization: Math.round(u * 100),
    borrow_rate: curve.borrow_rate_points[i] * 100,
  }));

  return (
    <div className="max-w-xl">
      <p className="text-xs font-medium text-text-secondary mb-2">
        {reserve.asset} — Borrow Rate vs Utilization
      </p>
      <ResponsiveContainer width="100%" height={160}>
        <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <XAxis
            dataKey="utilization"
            tick={{ fontSize: 9, fill: "#64748b" }}
            axisLine={{ stroke: "#282a3a" }}
            tickLine={false}
            tickFormatter={(v) => `${v}%`}
          />
          <YAxis
            tick={{ fontSize: 9, fill: "#64748b" }}
            axisLine={false}
            tickLine={false}
            width={40}
            tickFormatter={(v) => `${v.toFixed(0)}%`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#1e2030",
              border: "1px solid #282a3a",
              borderRadius: "6px",
              fontSize: "11px",
            }}
            formatter={(value: number) => [`${value.toFixed(2)}%`, "Borrow Rate"]}
            labelFormatter={(label) => `Utilization: ${label}%`}
          />
          <ReferenceLine
            x={Math.round(curve.optimal_utilization * 100)}
            stroke="#6366f1"
            strokeDasharray="4 4"
          />
          <ReferenceLine
            x={Math.round(reserve.utilization * 100)}
            stroke="#f97316"
            strokeDasharray="2 2"
          />
          <Line
            type="monotone"
            dataKey="borrow_rate"
            stroke="#eab308"
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
      <div className="flex gap-4 mt-1 text-[10px] text-text-muted">
        <span><span className="inline-block w-3 h-0.5 bg-[#f97316] mr-1 align-middle" />Current</span>
        <span><span className="inline-block w-3 h-0.5 bg-[#6366f1] mr-1 align-middle" style={{ borderTop: "1px dashed #6366f1" }} />Kink</span>
      </div>
    </div>
  );
}

function formatUsd(value: number): string {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}
